import * as React from "react";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { TextField, TextFieldProps, useTheme, alpha, useMediaQuery } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

export interface DatePickerBaseProps {
  label?: string;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
  inputFormat?: string;
  sx?: TextFieldProps["sx"];
  fullWidth?: boolean;
  size?: "small" | "medium";
  required?: boolean;
}

/**
 * Global DatePicker component with consistent styling
 * Compatible with react-hook-form Controller
 * Styled to match SelectBase and StyledTextField
 */
const DatePickerBase: React.FC<DatePickerBaseProps> = ({
  label,
  value,
  onChange,
  error = false,
  helperText,
  disabled = false,
  minDate,
  maxDate,
  inputFormat = "MM/DD/YYYY",
  sx,
  fullWidth = true,
  size = "small",
  required = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";
  const baseBorderColor = isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8);

  return (
    <DesktopDatePicker
      label={label}
      inputFormat={inputFormat}
      value={value}
      onChange={onChange}
      disabled={disabled}
      minDate={minDate ?? undefined}
      maxDate={maxDate ?? undefined}
      showToolbar={true}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          fullWidth={fullWidth}
          required={required}
          error={error}
          helperText={helperText}
          sx={{
            ...sx,
            "& .MuiOutlinedInput-root": {
              borderRadius: isMobile ? "8px" : isTablet ? "10px" : "12px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              padding: isMobile ? theme.spacing(1, 1.25) : theme.spacing(1.25, 1.5),
              // Glass effect background dengan gradient
              background: isDark
                ? `linear-gradient(135deg, ${alpha("#ffffff", 0.15)} 0%, ${alpha("#ffffff", 0.08)} 100%)`
                : `linear-gradient(135deg, ${alpha("#ffffff", 0.25)} 0%, ${alpha("#ffffff", 0.15)} 100%)`,
              backdropFilter: isMobile ? "blur(20px) saturate(160%)" : "blur(40px) saturate(180%)",
              WebkitBackdropFilter: isMobile ? "blur(20px) saturate(160%)" : "blur(40px) saturate(180%)",
              // Border dengan glass effect
              border: `${isMobile ? "1px" : "1.5px"} solid ${error
                ? theme.palette.error.main
                : baseBorderColor
                }`,
              boxShadow: error
                ? isMobile
                  ? `0 4px 16px ${alpha(theme.palette.error.main, 0.15)}, 0 1px 4px ${alpha(theme.palette.error.main, 0.08)}`
                  : `0 8px 32px ${alpha(theme.palette.error.main, 0.2)}, 0 2px 8px ${alpha(theme.palette.error.main, 0.1)}`
                : isDark
                  ? isMobile
                    ? "0 4px 16px rgba(0, 0, 0, 0.25), 0 1px 4px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.25)"
                    : "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                  : isMobile
                    ? "0 4px 16px rgba(244, 162, 89, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5)"
                    : "0 8px 32px rgba(244, 162, 89, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "50%",
                background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
                pointerEvents: "none",
                zIndex: 0,
              },
              "& .MuiInputBase-input": {
                position: "relative",
                zIndex: 1,
                fontSize: isMobile ? "0.875rem" : isTablet ? "0.9rem" : "0.95rem",
                padding: isMobile ? theme.spacing(1, 0) : theme.spacing(1.25, 0),
              },
              "&:hover": {
                transform: isMobile ? "none" : "translateY(-2px)",
                boxShadow: error
                  ? isMobile
                    ? `0 6px 24px ${alpha(theme.palette.error.main, 0.2)}, inset 0 1px 0 ${alpha(theme.palette.error.main, 0.25)}`
                    : `0 12px 48px ${alpha(theme.palette.error.main, 0.25)}, inset 0 1px 0 ${alpha(theme.palette.error.main, 0.3)}`
                  : isDark
                    ? isMobile
                      ? "0 6px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.35)"
                      : "0 12px 48px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
                    : isMobile
                      ? "0 6px 24px rgba(244, 162, 89, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6)"
                      : "0 12px 48px rgba(244, 162, 89, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
                border: `${isMobile ? "1px" : "1.5px"} solid ${error
                  ? theme.palette.error.main
                  : isDark
                    ? alpha("#ffffff", 0.5)
                    : alpha("#ffffff", 0.9)
                  }`,
                background: isDark
                  ? `linear-gradient(135deg, ${alpha("#ffffff", 0.18)} 0%, ${alpha("#ffffff", 0.1)} 100%)`
                  : `linear-gradient(135deg, ${alpha("#ffffff", 0.3)} 0%, ${alpha("#ffffff", 0.18)} 100%)`,
              },
              "&.Mui-focused": {
                transform: isMobile ? "none" : "translateY(-2px)",
                boxShadow: error
                  ? isMobile
                    ? `0 6px 24px ${alpha(theme.palette.error.main, 0.25)}, inset 0 1px 0 ${alpha(theme.palette.error.main, 0.35)}`
                    : `0 12px 48px ${alpha(theme.palette.error.main, 0.3)}, inset 0 1px 0 ${alpha(theme.palette.error.main, 0.4)}`
                  : isMobile
                    ? `0 6px 24px ${alpha("#F4A259", 0.15)}, inset 0 1px 0 rgba(255, 255, 255, 0.6)`
                    : `0 12px 48px ${alpha("#F4A259", 0.2)}, inset 0 1px 0 rgba(255, 255, 255, 0.7)`,
                border: `${isMobile ? "1px" : "1.5px"} solid ${error
                  ? theme.palette.error.main
                  : alpha("#ffffff", 0.95)
                  }`,
                background: isDark
                  ? `linear-gradient(135deg, ${alpha("#ffffff", 0.2)} 0%, ${alpha("#ffffff", 0.12)} 100%)`
                  : `linear-gradient(135deg, ${alpha("#ffffff", 0.35)} 0%, ${alpha("#ffffff", 0.22)} 100%)`,
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: isMobile ? "8px" : isTablet ? "10px" : "12px",
              border: "none",
            },
            "& .MuiInputLabel-root": {
              zIndex: 1,
              left: isMobile ? theme.spacing(1.25) : theme.spacing(1.4),
              top: isMobile ? theme.spacing(0.75) : theme.spacing(0.9),
              padding: 0,
              color: error
                ? theme.palette.error.main
                : theme.palette.text.secondary,
              background: "transparent",
              transformOrigin: "top left",
              transition: "all 0.2s ease",
              fontSize: isMobile ? "0.8rem" : isTablet ? "0.875rem" : "0.95rem",
              fontWeight: 500,
              maxWidth: "calc(100% - 48px)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              [theme.breakpoints.down("sm")]: {
                maxWidth: "calc(100% - 28px)",
                wordBreak: "break-word",
                whiteSpace: "normal",
                overflow: "visible",
                textOverflow: "clip",
                lineHeight: 1.2,
              },
              "&.Mui-focused": {
                color: error
                  ? theme.palette.error.main
                  : "#F4A259",
              },
              "&.MuiInputLabel-shrink": {
                top: isMobile ? theme.spacing(-1) : theme.spacing(-1.2),
                transform: "translateY(0) scale(0.86)",
                padding: isMobile ? theme.spacing(0, 0.5) : theme.spacing(0, 0.75),
                borderRadius: 999,
                width: "fit-content",
                maxWidth: isMobile ? "85%" : isTablet ? "88%" : "90%",
                display: isMobile ? "block" : "inline-flex",
                fontSize: isMobile ? "0.75rem" : isTablet ? "0.8rem" : "0.88rem",
                fontWeight: 500,
                color: error
                  ? theme.palette.error.main
                  : "#F4A259",
                background: theme.palette.mode === "dark"
                  ? alpha("#ffffff", 0.15)
                  : alpha("#ffffff", 0.98),
                boxShadow: theme.palette.mode === "dark"
                  ? isMobile
                    ? "0 1px 3px rgba(0,0,0,0.3)"
                    : "0 1px 4px rgba(0,0,0,0.35)"
                  : isMobile
                    ? "0 1px 3px rgba(0,0,0,0.12)"
                    : "0 1px 4px rgba(0,0,0,0.15)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                [theme.breakpoints.down("sm")]: {
                  maxWidth: "85%",
                  whiteSpace: "nowrap",
                  wordBreak: "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  lineHeight: 1.3,
                  width: "auto",
                },
                [theme.breakpoints.between("sm", "md")]: {
                  maxWidth: "88%",
                },
              },
            },
            "& .MuiFormHelperText-root": {
              marginTop: isMobile ? theme.spacing(0.5) : theme.spacing(0.75),
              marginLeft: isMobile ? theme.spacing(1.25) : theme.spacing(1.4),
              fontSize: isMobile ? "0.7rem" : "0.75rem",
            },
          }}
        />
      )}
    />
  );
};

export default DatePickerBase;

